import { addIf } from '@/src/utils/list.utils';
import { Badge, Button } from '@amzn/awsui-components-react';
import moment from 'moment';
import { Challenge, ChallengeReviewStatus, ChallengeStatus } from '../../../../types/Challenge';
import { Nullable } from '../../../../types/common';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { TFunctionProvider } from '../../../common/TFunctionProvider';

export const getReviewStatus: (
  status: Nullable<ChallengeReviewStatus> | Nullable<ChallengeStatus> | undefined
) => JSX.Element = (status) => {
  if (!status) {
    return <div>--</div>;
  }

  switch (status) {
    case ChallengeStatus.APPROVED:
    case ChallengeReviewStatus.APPROVED:
      return (
        <div className="aws-positive-green">
          <Badge color="green">
            <TFunctionProvider>{(tr) => tr(i18nKeys.general.approved)}</TFunctionProvider>
          </Badge>
        </div>
      );
    case ChallengeStatus.IN_REVIEW:
    case ChallengeReviewStatus.IN_REVIEW:
      return (
        <div className="subtle">
          <Badge>
            <TFunctionProvider>{(tr) => tr(i18nKeys.general.inReview)}</TFunctionProvider>
          </Badge>
        </div>
      );
    case ChallengeStatus.READY_FOR_REVIEW:
    case ChallengeReviewStatus.READY_FOR_REVIEW:
      return (
        <div className="info">
          <Badge color="blue">
            <TFunctionProvider>{(tr) => tr(i18nKeys.general.readyForReview)}</TFunctionProvider>
          </Badge>
        </div>
      );
    case ChallengeStatus.NEEDS_WORK:
    case ChallengeReviewStatus.NEEDS_WORK:
      return (
        <div className="warning">
          <Badge color="red">
            <TFunctionProvider>{(tr) => tr(i18nKeys.general.needsWork)}</TFunctionProvider>
          </Badge>
        </div>
      );
    case ChallengeReviewStatus.NOT_REVIEWED:
      return (
        <div className="subtle">
          <Badge>
            <TFunctionProvider>{(tr) => tr(i18nKeys.general.notReviewed)}</TFunctionProvider>
          </Badge>
        </div>
      );
    case ChallengeStatus.DRAFT:
      return (
        <div className="subtle">
          <Badge>
            <TFunctionProvider>{(tr) => tr(i18nKeys.general.draft)}</TFunctionProvider>
          </Badge>
        </div>
      );
    default:
      return (
        <div className="grey">
          <Badge>
            <TFunctionProvider>{(tr) => tr(i18nKeys.general.pending)}</TFunctionProvider>
          </Badge>
        </div>
      );
  }
};

export const COLUMN_DEFINITIONS = (
  showLcsrTicket: boolean,
  handleRevisionSelection: (challengeRevision: Challenge) => void
) => {
  return [
    {
      id: 'version',
      sortingField: 'version',
      header: (
        <TFunctionProvider>{(t) => t(i18nKeys.challenges.challengeDetails.table.titles.version)}</TFunctionProvider>
      ),
      cell: (item: Challenge) => (
        <Button
          id="challenge-review-minor-version"
          data-testid="challenge-review-minor-version"
          variant="link"
          onClick={() => handleRevisionSelection(item)}
        >{`${item?.majorVersion}.${item?.minorVersion}`}</Button>
      ),
    },
    {
      id: 'status',
      header: (
        <TFunctionProvider>{(t) => t(i18nKeys.challenges.challengeDetails.table.titles.status)}</TFunctionProvider>
      ),
      cell: (item: Challenge) => getReviewStatus(item.status),
    },
    ...addIf(showLcsrTicket, {
      id: 'lcsrTicket',
      header: (
        <TFunctionProvider>{(t) => t(i18nKeys.challenges.challengeDetails.table.titles.lcsrTicket)}</TFunctionProvider>
      ),
      cell: (item: Challenge) => item.props.lcsrTicket || '-',
    }),
    {
      id: 'timeCreated',
      sortingField: 'timeCreated',
      header: (
        <TFunctionProvider>{(t) => t(i18nKeys.challenges.challengeDetails.table.titles.timeCreated)}</TFunctionProvider>
      ),
      cell: (item: Challenge) => moment(item?.createdDate).format('lll'),
      allowLineWrap: true,
    },
    {
      id: 'changedBy',
      sortingField: 'changedBy',
      header: (
        <TFunctionProvider>{(t) => t(i18nKeys.challenges.challengeDetails.table.titles.changedBy)}</TFunctionProvider>
      ),
      cell: (item: Challenge) => item?.props.lastUpdatedBy || '--',
      allowLineWrap: true,
    },
  ];
};
